import { useState } from 'react';
import styled from 'styled-components';

import { MenuCheckboxItem } from '@creditornot/cb-menu';
import { Spinner } from '@creditornot/cb-progress';
import { CountryCode } from 'common/api-types/common';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { Divider } from 'common/components/cookbook/Divider';
import { LoadingBar } from 'common/components/cookbook/LoadingBar';
import { SearchInput } from 'common/components/cookbook/Search/SearchInput';
import { Stack } from 'common/components/cookbook/Stack';
import { useCountriesQuery } from 'common/hooks/useCountries';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';

import { BrandsFilterList } from './BrandsFilterList';
import { useBrandsQuery } from './query';

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
`;

type Props = {
  brandIds?: string[];
  /**
   * If provided, only brands with active products in these countries are shown. E.g. used in pricing tool.
   */
  countriesWithActiveProducts?: CountryCode[];
  onBrandChange: (ids: string[]) => void;
  noBrand?: boolean;
  onNoBrandChange?: (noBrand: boolean) => void;
};

export function BrandsFilter({
  brandIds = [],
  countriesWithActiveProducts,
  onBrandChange,
  noBrand = false,
  onNoBrandChange,
}: Props) {
  const f = useFormatMessage();
  const selectedCountryCodes = useSelectedCountryCodes();
  const { data: countries } = useCountriesQuery();

  const selectedCountryDefaultLanguageCode = countries?.find(
    (country) => country.countryCode === selectedCountryCodes[0],
  )?.defaultLanguageCode;

  const languageCode =
    selectedCountryCodes.length === 1 && selectedCountryDefaultLanguageCode
      ? selectedCountryDefaultLanguageCode
      : 'en';

  const {
    data: brands,
    isRefetching,
    isLoading,
    error,
  } = useBrandsQuery({
    languageCode,
    countriesWithActiveProducts,
  });

  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);

  const filteredBrandsByName = brands?.filter((brand) =>
    brand.name.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
  );

  const formattedBrands = filteredBrandsByName?.map((brand) => {
    return {
      id: brand.id,
      name: `${brand.name}${brand.owner.name ? ` (${brand.owner.name})` : ''}`,
    };
  });

  const toggle = (id: string) => {
    const newValues = brandIds.includes(id)
      ? brandIds.filter((brandId) => brandId !== id)
      : [...brandIds, id];

    onBrandChange(newValues);
  };

  return (
    <Stack gap="regularMedium">
      {isLoading && (
        <SpinnerContainer data-test-id="brands-filter-spinner">
          <Spinner size="large" />
        </SpinnerContainer>
      )}
      {isRefetching && <LoadingBar />}
      {onNoBrandChange && (
        <>
          <MenuCheckboxItem
            role="checkbox"
            checked={noBrand}
            onClick={() => onNoBrandChange(!noBrand)}
          >
            {f('catalog.filter_products_without', { attribute: 'brand' })}
          </MenuCheckboxItem>
          <Divider style={{ margin: '8px -8px' }} />
        </>
      )}
      <SearchInput
        aria-label={f('components.brands_filter.search_brands')}
        value={searchQuery}
        onChange={(value) => setSearchQuery(value)}
      />
      <ApiErrorNotification error={error} />
      {formattedBrands && (
        <BrandsFilterList brands={formattedBrands} onClick={toggle} selectedBrandIds={brandIds} />
      )}
    </Stack>
  );
}
